import { Draggable } from "@hello-pangea/dnd";
import { Link } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GripVertical, CalendarClock, MessageSquare, Mail, Phone, Bell, CheckCircle2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDateOnly } from "@/utils/dateUtils";
import { DisponibilidadeLinha } from "@/components/freelancers/DisponibilidadeBadge";
import type { DisponibilidadeSummary } from "@/utils/disponibilidadeUtils";
import type { ReconsultaItem } from "@/hooks/useDisponibilidade";

interface Props {
  item: ReconsultaItem;
  summary?: DisponibilidadeSummary | null;
  index: number;
  onReconsultar: (item: ReconsultaItem) => void;
  onCancelar: (checkId: string) => void;
}

export function ReconsultaKanbanCard({ item, summary, index, onReconsultar, onCancelar }: Props) {
  const pendente = item.status === "pendente";
  const soPhone = item.telefone?.replace(/\D/g, "");

  return (
    <Draggable draggableId={item.id} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={cn(
            "hover:shadow-md transition-all",
            !pendente && "opacity-75",
            snapshot.isDragging && "shadow-lg ring-2 ring-primary rotate-2"
          )}
        >
          <CardContent className="p-3 space-y-2">
            {/* Header: grip + nome */}
            <div className="flex items-start gap-2">
              <div
                {...provided.dragHandleProps}
                className="cursor-grab active:cursor-grabbing p-0.5 -ml-1 text-muted-foreground/50 hover:text-muted-foreground"
              >
                <GripVertical className="h-4 w-4" />
              </div>
              <Link
                to={`/freelancers/${item.freelancer_id}`}
                onClick={(event) => event.stopPropagation()}
                className="font-semibold text-sm truncate flex-1 hover:text-primary hover:underline"
              >
                {item.nome}
              </Link>
            </div>

            <DisponibilidadeLinha summary={summary ?? null} level="chip" />

            {/* Data prevista (pendente) ou resolvida (feito/cancelado) */}
            {pendente ? (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CalendarClock className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                <span>Prevista: {formatDateOnly(item.due_at)}</span>
              </div>
            ) : (
              item.resolved_at && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    {item.status === "feito" ? "Reconsultado" : "Cancelado"} em{" "}
                    {formatDateOnly(item.resolved_at)}
                  </span>
                </div>
              )
            )}

            {/* Nota */}
            {item.note && (
              <div className="flex items-start gap-1.5 text-xs text-muted-foreground bg-muted/50 p-1.5 rounded">
                <MessageSquare className="h-3 w-3 shrink-0 mt-0.5" />
                <span className="line-clamp-2">{item.note}</span>
              </div>
            )}

            {/* Contatos rápidos */}
            {(item.email || soPhone) && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {item.email && (
                  <a
                    href={`mailto:${item.email}`}
                    onClick={(event) => event.stopPropagation()}
                    className="flex items-center gap-1 hover:text-primary hover:underline truncate"
                  >
                    <Mail className="h-3 w-3 shrink-0" />
                    <span className="truncate">{item.email}</span>
                  </a>
                )}
                {soPhone && (
                  <a
                    href={`https://wa.me/55${soPhone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="flex items-center gap-1 hover:text-green-600 hover:underline shrink-0"
                  >
                    <Phone className="h-3 w-3 shrink-0" />
                    WhatsApp
                  </a>
                )}
              </div>
            )}

            {/* Ações (só nas pendentes) */}
            {pendente && (
              <div className="flex items-center gap-1.5 pt-1">
                <Button
                  size="sm"
                  onClick={() => onReconsultar(item)}
                  className="flex-1 h-7 text-xs gap-1.5"
                >
                  <Bell className="h-3.5 w-3.5" />
                  Reconsultado
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onCancelar(item.id)}
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-red-600"
                  title="Cancelar reconsulta"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            {item.status === "feito" && (
              <div className="flex items-center gap-1.5 text-xs text-green-600 pt-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Resolvida
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </Draggable>
  );
}
